import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import Table, {
  TableManipulationItem,
  TableManipulationTopContainer,
  TableRowCell,
} from 'core-system/Table'
import palette from 'core-system/Themes/palette'
import { TransitException, TransitExceptionStatus } from '../dashboardTypes'
import ExceptionQueuedRow from './ExceptionQueuedRow'

type ExceptionStatusFilter = TransitExceptionStatus | 'ALL'

const Section = styled.div`
  margin-bottom: 1.5rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.625rem;
  border-radius: 999px;
  background: ${palette.primary.pink100};
  color: ${palette.primary.pink700};
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
`

const FilterTabs = styled(TableManipulationTopContainer)`
  margin-bottom: 1rem !important;
  border: 0;
  padding: 0;
`

const HeaderSpan = styled(Text)`
  margin-left: 0.5rem;
`

const getHeaderColor = (active: boolean) =>
  active ? palette.white : palette.secondary.purple700

const StatusFilterItem = ({
  text,
  isActive,
  count,
  onClick,
}: {
  text: string
  isActive: boolean
  count: number
  onClick: () => void
}) => (
  <TableManipulationItem onClick={onClick} active={isActive}>
    {count}
    <HeaderSpan variant='action4' color={getHeaderColor(isActive)}>
      {text}
    </HeaderSpan>
  </TableManipulationItem>
)

interface ExceptionCardProps {
  exceptions?: TransitException[] | null
  onViewAllClick?: () => void
}

const ExceptionCard = ({
  exceptions = null,
  onViewAllClick,
}: ExceptionCardProps) => {
  const [statusFilter, setStatusFilter] = useState<ExceptionStatusFilter>('ALL')
  const isLoading = exceptions === null
  const allExceptions = exceptions || []

  const openCount = allExceptions.filter((e) => e.status === 'OPEN').length
  const closedCount = allExceptions.filter((e) => e.status === 'RESOLVED').length
  const filteredExceptions =
    statusFilter === 'ALL'
      ? allExceptions
      : allExceptions.filter((e) => e.status === statusFilter)
  const itemCount = filteredExceptions.length
  const countLabel = itemCount === 1 ? '1 item' : `${itemCount} items`

  const headerCells = useMemo(
    () => [
      <TableRowCell key='type'>Type</TableRowCell>,
      <TableRowCell key='message'>Issue</TableRowCell>,
      <TableRowCell key='action'>Action</TableRowCell>,
      <TableRowCell key='openFor'>Open for</TableRowCell>,
      <TableRowCell key='status'>Status</TableRowCell>,
    ],
    []
  )

  const rows = useMemo(
    () =>
      filteredExceptions.map((exception) => (
        <ExceptionQueuedRow key={exception.id} exception={exception} />
      )),
    [filteredExceptions]
  )

  const emptyMessage =
    statusFilter === 'OPEN'
      ? 'No open exceptions'
      : statusFilter === 'RESOLVED'
        ? 'No closed exceptions'
        : 'No exceptions'

  return (
    <Section data-cy='exception-card'>
      <Header>
        <TitleGroup>
          <Text variant='h3' fontWeight={500}>
            Command Center
          </Text>
          <CountBadge data-cy='exception-card-count'>{countLabel}</CountBadge>
        </TitleGroup>
        <Button variant='secondary' size='small' onClick={onViewAllClick}>
          View all employees →
        </Button>
      </Header>
      <FilterTabs>
        <StatusFilterItem
          text='All'
          isActive={statusFilter === 'ALL'}
          count={allExceptions.length}
          onClick={() => setStatusFilter('ALL')}
        />
        <StatusFilterItem
          text='Open'
          isActive={statusFilter === 'OPEN'}
          count={openCount}
          onClick={() => setStatusFilter('OPEN')}
        />
        <StatusFilterItem
          text='Closed'
          isActive={statusFilter === 'RESOLVED'}
          count={closedCount}
          onClick={() => setStatusFilter('RESOLVED')}
        />
      </FilterTabs>
      <Table
        headerCells={headerCells}
        rows={rows}
        maxRows={5}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
      />
    </Section>
  )
}

export default React.memo(ExceptionCard)
